import React, { useState, useEffect } from 'react'
import { bankQuestionService } from '../../services/bankQuestionService_moi'

const ImageDebugTest: React.FC = () => {
  const [questions, setQuestions] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const testLoadQuestions = async () => {
    setLoading(true)
    setError(null)
    
    try {
      console.log('🔍 Testing image loading from database...')
      
      // Test load questions from different sources
      const testCases = [
        { lop: '11', chuong: '1', bai: '01', count: 5 },
        { lop: '11', chuong: '1', bai: '04', count: 5 },
        { lop: '11', chuong: '2', bai: '07', count: 5 }
      ]
      
      const allQuestions: any[] = []
      
      for (const testCase of testCases) {
        console.log(`📚 Loading questions for L${testCase.lop} C${testCase.chuong} B${testCase.bai}`)
        
        const quizData = await bankQuestionService.getRandomQuestions(
          testCase.lop,
          testCase.chuong,
          testCase.bai,
          {
            thoiGianLamBai: 30,
            diemDat: 70
          }
        )
        
        if (quizData && quizData.cauHoi) {
          console.log(`✅ Loaded ${quizData.cauHoi.length} questions`)
          
          // Filter questions that have images
          const questionsWithImages = quizData.cauHoi.filter(q => 
            q.hinh_anh || q.hinh_anh_giai_thich
          )
          
          console.log(`🖼️ Questions with images: ${questionsWithImages.length}`)
          
          questionsWithImages.forEach(q => {
            console.log(`Question ${q.id}:`, {
              hasQuestionImage: !!q.hinh_anh,
              questionImage: q.hinh_anh,
              hasExplanationImage: !!q.hinh_anh_giai_thich,
              explanationImage: q.hinh_anh_giai_thich
            })
          })
          
          allQuestions.push(...quizData.cauHoi.map(q => ({
            ...q,
            source: `L${testCase.lop}_C${testCase.chuong}_B${testCase.bai}`
          })))
        }
      }
      
      setQuestions(allQuestions)
      
    } catch (err) {
      console.error('❌ Error loading questions:', err)
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    testLoadQuestions()
  }, [])

  const questionsWithImages = questions.filter(q => q.hinh_anh || q.hinh_anh_giai_thich)

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Image Debug Test</h1>
      
      <div className="mb-6">
        <button
          onClick={testLoadQuestions}
          disabled={loading}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
        >
          {loading ? 'Loading...' : 'Reload Questions'}
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Summary */}
        <div className="bg-gray-100 p-4 rounded">
          <h2 className="text-lg font-semibold mb-2">Summary</h2>
          <p>Total questions loaded: <strong>{questions.length}</strong></p>
          <p>Questions with images: <strong>{questionsWithImages.length}</strong></p>
          <p>Questions with question images: <strong>{questions.filter(q => q.hinh_anh).length}</strong></p>
          <p>Questions with explanation images: <strong>{questions.filter(q => q.hinh_anh_giai_thich).length}</strong></p>
        </div>

        {/* Debug Info */}
        <div className="bg-blue-50 p-4 rounded">
          <h2 className="text-lg font-semibold mb-2">Debug Info</h2>
          <p>Check browser console for detailed logs</p>
          <p>Image paths should be relative to /public</p>
        </div>
      </div>

      {/* Questions with Images */}
      {questionsWithImages.length > 0 && (
        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-4">Questions with Images ({questionsWithImages.length})</h2>
          
          <div className="space-y-6">
            {questionsWithImages.map((question, index) => (
              <div key={question.id} className="border border-gray-300 rounded-lg p-4">
                <div className="mb-4">
                  <h3 className="font-semibold text-lg">
                    Question {index + 1} (ID: {question.id})
                  </h3>
                  <p className="text-sm text-gray-600">Source: {question.source}</p>
                </div>

                <div className="mb-4">
                  <h4 className="font-medium mb-2">Question Content:</h4>
                  <p className="text-gray-800">{question.cau_hoi?.substring(0, 200)}...</p>
                </div>

                {/* Question Image */}
                {question.hinh_anh && (
                  <div className="mb-4">
                    <h4 className="font-medium mb-2">Question Image:</h4>
                    <p className="text-sm text-gray-600 mb-2">Path: {question.hinh_anh}</p>
                    <div className="border border-gray-200 p-2 rounded">
                      <img
                        src={question.hinh_anh}
                        alt="Question image"
                        className="max-w-full h-auto"
                        onLoad={() => console.log(`✅ Image loaded: ${question.hinh_anh}`)}
                        onError={(e) => {
                          console.error(`❌ Failed to load image: ${question.hinh_anh}`)
                          const target = e.currentTarget as HTMLImageElement
                          target.style.display = 'none'
                          const errorDiv = document.createElement('div')
                          errorDiv.className = 'text-red-500 text-sm p-2'
                          errorDiv.textContent = `❌ Failed to load: ${question.hinh_anh}`
                          target.parentNode?.appendChild(errorDiv)
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Explanation Image */}
                {question.hinh_anh_giai_thich && (
                  <div className="mb-4">
                    <h4 className="font-medium mb-2">Explanation Image:</h4>
                    <p className="text-sm text-gray-600 mb-2">Path: {question.hinh_anh_giai_thich}</p>
                    <div className="border border-gray-200 p-2 rounded">
                      <img
                        src={question.hinh_anh_giai_thich}
                        alt="Explanation image"
                        className="max-w-full h-auto"
                        onLoad={() => console.log(`✅ Explanation image loaded: ${question.hinh_anh_giai_thich}`)}
                        onError={(e) => {
                          console.error(`❌ Failed to load explanation image: ${question.hinh_anh_giai_thich}`)
                          const target = e.currentTarget as HTMLImageElement
                          target.style.display = 'none'
                          const errorDiv = document.createElement('div')
                          errorDiv.className = 'text-red-500 text-sm p-2'
                          errorDiv.textContent = `❌ Failed to load: ${question.hinh_anh_giai_thich}`
                          target.parentNode?.appendChild(errorDiv)
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Questions (for debugging) */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold mb-4">All Questions Debug</h2>
        <div className="bg-gray-50 p-4 rounded max-h-96 overflow-y-auto">
          <pre className="text-xs">
            {JSON.stringify(questions.slice(0, 3), null, 2)}
          </pre>
        </div>
      </div>
    </div>
  )
}

export default ImageDebugTest